// API response types matching the Pages transport and index builder.

// The `/api` client functions below target the same-origin Pages Function
// transport (web/functions/api/, see lib/api-transport.ts). Match-tier routes
// are proxied to acb-api; if that backend is not configured or ready, the
// explicit 503 response surfaces as MatchTierOfflineError instead of letting
// an SPA fallback look like a successful API response.
import {
  requireApiTransport,
  MatchTierOfflineError,
  matchTierOfflineMessage,
} from './lib/api-transport';

/** True when the response carries a real JSON body (the SPA fallback is HTML). */
function isJsonResponse(response: Response): boolean {
  return (response.headers.get('content-type') ?? '').includes('application/json');
}

/** Throws MatchTierOfflineError when this 503 response is a match-tier gate. */
async function throwIfMatchTierOffline(response: Response): Promise<void> {
  if (response.status !== 503) return;
  const body: unknown = await response.json().catch(() => null);
  const msg = matchTierOfflineMessage(response.status, body);
  if (msg) throw new MatchTierOfflineError(msg);
}

// Leaderboard types
export interface LeaderboardEntry {
  rank: number;
  bot_id: string;
  name: string;
  owner_id: string;
  rating: number;
  rating_deviation: number;
  matches_played: number;
  matches_won: number;
  win_rate: number;
  health_status: string;
}

export interface LeaderboardIndex {
  updated_at: string;
  entries: LeaderboardEntry[];
}

// Bot profile types
export interface RatingHistoryEntry {
  bot_id: string;
  rating: number;
  rating_deviation: number;
  recorded_at: string;
}

export interface MatchSummaryParticipant {
  bot_id: string;
  name: string;
  score: number;
  won: boolean;
}

export interface MatchSummary {
  id: string;
  completed_at: string | null;
  participants: MatchSummaryParticipant[];
  winner_id: string | null;
  map_id?: string;
  turns: number | null;
  end_reason: string | null;
  enriched?: boolean;
}

export interface BotProfile {
  id: string;
  name: string;
  owner_id: string;
  rating: number;
  rating_deviation: number;
  rating_volatility: number;
  matches_played: number;
  matches_won: number;
  win_rate: number;
  health_status: string;
  created_at: string;
  updated_at: string;
  rating_history: RatingHistoryEntry[];
  recent_matches: MatchSummary[];
  // Evolution fields (optional - only present for evolved bots)
  evolved?: boolean;
  island?: string;
  generation?: number;
  parent_ids?: string[];
  // §14.1: debug telemetry public-visibility toggle
  debug_public?: boolean;
}

export interface BotDirectoryEntry {
  id: string;
  name: string;
  rating: number;
  matches_played: number;
  win_rate: number;
}

export interface BotDirectory {
  updated_at: string;
  bots: BotDirectoryEntry[];
}

// Match index types
export interface MatchIndex {
  updated_at: string;
  matches: MatchSummary[];
}

// Registration types
export interface RegisterRequest {
  name: string;
  endpoint_url: string;
  /** Matches cmd/acb-api's registration contract. */
  owner: string;
  debug_public?: boolean;
}

export interface RegisterResponse {
  success: boolean;
  bot_id?: string;
  /** Delivered exactly once by registration or rotation. */
  shared_secret?: string;
  /** Kept as a read-compatibility alias for older API deployments. */
  api_key?: string;
  error?: string;
  code?: string;
}

// Evolution dashboard types (re-exported from types.ts for convenience)
export type {
  LiveJSON,
  EvolutionIslandStat as IslandStat,
  EvolutionParentInfo as ParentInfo,
  EvolutionStageResult as StageResult,
  EvolutionValidationStatus as ValidationStatus,
  EvaluationMatchResult as MatchResult,
  EvolutionEvaluationStatus as EvaluationStatus,
  EvolutionCandidate as Candidate,
  EvolutionCycleInfo as CycleInfo,
  EvolutionActivityEntry as ActivityEntry,
  EvolutionTotals as Totals,
  EvolutionGenerationEntry as GenerationEntry,
  EvolutionLineageNode as LineageNode,
  EvolutionMetaSnapshot as MetaSnapshot,
} from './types';

// Convenience alias: the full live.json document
import type { LiveJSON } from './types';
export type EvolutionLiveData = LiveJSON;

// Full island stat (legacy format, not in live.json schema)
export interface IslandStatFull {
  count: number;
  best_fitness: number;
  avg_fitness: number;
  diversity: number;
  promoted_count: number;
}

// Blog / Narrative Engine types

export interface BlogWeekStats {
  matches_played: number;
  top_bot: string;
  top_bot_rating: number;
  biggest_upset: string | null;
  most_active_bot: string;
  most_active_bot_matches: number;
  island_leader: string | null;
}

export interface BlogPost {
  slug: string;
  title: string;
  published_at: string;
  type: 'meta-report' | 'chronicle';
  summary: string;
  body_markdown: string;
  tags: string[];
}

export interface BlogIndex {
  updated_at: string;
  posts: BlogPost[];
}

// API configuration
export const API_BASE = '/api';

// ─── Stale-while-revalidate cache ─────────────────────────────────────────────────
// Returns cached data instantly (if available) while fetching fresh data in the
// background.  On the next call the fresh data is already in cache.  This gives
// sub-ms render times for repeat visits while keeping data reasonably current.

interface CacheEntry<T> { data: T; ts: number }

const swrCache = new Map<string, CacheEntry<unknown>>();
const SWR_MAX_AGE = 5 * 60 * 1000; // 5 min — data is served from cache without re-fetch

function swr<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const cached = swrCache.get(key) as CacheEntry<T> | undefined;

  if (cached) {
    // Serve stale immediately, revalidate in background if older than max-age
    if (Date.now() - cached.ts > SWR_MAX_AGE) {
      fetcher().then(data => swrCache.set(key, { data, ts: Date.now() })).catch(() => {});
    }
    return Promise.resolve(cached.data);
  }

  // No cache — fetch and cache
  return fetcher().then(data => {
    swrCache.set(key, { data, ts: Date.now() });
    return data;
  });
}

/** Seed the SWR cache with pre-fetched data (used by hover preloader). */
export function seedSwrCache(key: string, data: unknown): void {
  if (!swrCache.has(key)) {
    swrCache.set(key, { data, ts: Date.now() });
  }
}

// API client functions
export async function fetchLeaderboard(): Promise<LeaderboardIndex> {
  return swr('leaderboard', async () => {
    const response = await fetch('/data/leaderboard.json');
    if (!response.ok) throw new Error(`Failed to fetch leaderboard: ${response.status}`);
    return response.json();
  });
}

export async function fetchBotDirectory(): Promise<BotDirectory> {
  return swr('bot-directory', async () => {
    const response = await fetch('/data/bots/index.json');
    if (!response.ok) throw new Error(`Failed to fetch bot directory: ${response.status}`);
    return response.json();
  });
}

export async function fetchBotProfile(botId: string): Promise<BotProfile> {
  return swr(`bot-${botId}`, async () => {
    const response = await fetch(`/data/bots/${botId}.json`);
    if (!response.ok) throw new Error(`Failed to fetch bot profile: ${response.status}`);
    return response.json();
  });
}

export async function fetchMatchIndex(): Promise<MatchIndex> {
  return swr('match-index', async () => {
    const response = await fetch('/data/matches/index.json');
    if (!response.ok) throw new Error(`Failed to fetch match index: ${response.status}`);
    return response.json();
  });
}

export async function registerBot(request: RegisterRequest): Promise<RegisterResponse> {
  requireApiTransport('Bot registration');
  const response = await fetch(`${API_BASE}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!isJsonResponse(response)) {
    return { success: false, error: `Registration failed: unexpected non-JSON response (${response.status})` };
  }
  const body = await response.json().catch(() => null) as Record<string, unknown> | null;
  if (!body || typeof body !== 'object') {
    return { success: false, error: `Registration failed: invalid JSON response (${response.status})` };
  }
  if (!response.ok) {
    return {
      success: false,
      error: typeof body.error === 'string' ? body.error : `Registration failed: ${response.status}`,
      code: typeof body.code === 'string' ? body.code : undefined,
    };
  }
  return {
    success: true,
    bot_id: typeof body.bot_id === 'string' ? body.bot_id : undefined,
    shared_secret: typeof body.shared_secret === 'string' ? body.shared_secret : undefined,
    api_key: typeof body.api_key === 'string' ? body.api_key : undefined,
  };
}

// Evolution live data is bundled into the Pages deploy under /data/ by the index builder.
// The evolver writes live.json every cycle with Cache-Control: max-age=10.
const DATA_BASE_URL = '/data';

export async function fetchEvolutionData(): Promise<EvolutionLiveData> {
  // Evolution data changes every ~10s — bypass SWR, always fetch fresh
  const response = await fetch(`${DATA_BASE_URL}/evolution/live.json`);
  if (!response.ok) throw new Error(`Failed to fetch evolution data: ${response.status}`);
  return response.json();
}

export async function fetchBlogIndex(): Promise<BlogIndex> {
  return swr('blog-index', async () => {
    const response = await fetch('/data/blog/index.json');
    if (!response.ok) throw new Error(`Failed to fetch blog index: ${response.status}`);
    return response.json();
  });
}

export async function fetchBlogPost(slug: string): Promise<BlogPost> {
  return swr(`blog-${slug}`, async () => {
    const response = await fetch(`/data/blog/${slug}.json`);
    if (!response.ok) throw new Error(`Failed to fetch blog post: ${response.status}`);
    return response.json();
  });
}

export async function rotateApiKey(botId: string, currentKey: string): Promise<RegisterResponse> {
  requireApiTransport('API key rotation');
  const response = await fetch(`${API_BASE}/rotate-key`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bot_id: botId, shared_secret: currentKey }),
  });
  if (!isJsonResponse(response)) {
    return { success: false, error: `Key rotation failed: unexpected non-JSON response (${response.status})` };
  }
  const body = await response.json().catch(() => null) as Record<string, unknown> | null;
  if (!body || typeof body !== 'object') {
    return { success: false, error: `Key rotation failed: invalid JSON response (${response.status})` };
  }
  if (!response.ok) {
    return {
      success: false,
      error: typeof body.error === 'string' ? body.error : `Key rotation failed: ${response.status}`,
      code: typeof body.code === 'string' ? body.code : undefined,
    };
  }
  return {
    success: true,
    bot_id: typeof body.bot_id === 'string' ? body.bot_id : undefined,
    shared_secret: typeof body.shared_secret === 'string' ? body.shared_secret : undefined,
    api_key: typeof body.api_key === 'string' ? body.api_key : undefined,
  };
}

export interface RevokeResponse {
  success: boolean;
  bot_id?: string;
  status?: 'retired';
  error?: string;
  code?: string;
}

export async function revokeApiKey(botId: string, currentKey: string): Promise<RevokeResponse> {
  requireApiTransport('API credential revocation');
  const response = await fetch(`${API_BASE}/revoke-key`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bot_id: botId, shared_secret: currentKey }),
  });
  if (!isJsonResponse(response)) {
    return { success: false, error: `Credential revocation failed: unexpected non-JSON response (${response.status})` };
  }
  const body = await response.json().catch(() => null) as Record<string, unknown> | null;
  if (!body || typeof body !== 'object') {
    return { success: false, error: `Credential revocation failed: invalid JSON response (${response.status})` };
  }
  if (!response.ok) {
    return {
      success: false,
      error: typeof body.error === 'string' ? body.error : `Credential revocation failed: ${response.status}`,
      code: typeof body.code === 'string' ? body.code : undefined,
    };
  }
  return {
    success: true,
    bot_id: typeof body.bot_id === 'string' ? body.bot_id : undefined,
    status: body.status === 'retired' ? 'retired' : undefined,
  };
}

// Playlist types

export type PlaylistCategory =
  | 'featured'
  | 'rivalry'
  | 'upsets'
  | 'comebacks'
  | 'domination'
  | 'close_games'
  | 'long_games'
  | 'tutorial'
  | 'season'
  | 'weekly';

export interface PlaylistMatch {
  match_id: string;
  order: number;
  title?: string;
  thumbnail_url?: string;
  curation_tag?: string;
  participants?: { bot_id: string; name: string; score: number; won: boolean }[];
  score?: string;
  turns?: number;
  end_reason?: string;
  completed_at?: string;
}

export interface Playlist {
  slug: string;
  title: string;
  description: string;
  category: PlaylistCategory;
  match_count: number;
  created_at: string;
  updated_at: string;
  matches: PlaylistMatch[];
}

export interface PlaylistSummary {
  slug: string;
  title: string;
  description: string;
  category: PlaylistCategory;
  match_count: number;
  updated_at: string;
  thumbnail_match_id?: string;
}

export interface PlaylistIndex {
  updated_at: string;
  playlists: PlaylistSummary[];
}

export async function fetchPlaylistIndex(): Promise<PlaylistIndex> {
  return swr('playlist-index', async () => {
    const response = await fetch('/data/playlists/index.json');
    if (!response.ok) throw new Error(`Failed to fetch playlist index: ${response.status}`);
    return response.json();
  });
}

export async function fetchPlaylist(slug: string): Promise<Playlist> {
  return swr(`playlist-${slug}`, async () => {
    const response = await fetch(`/data/playlists/${slug}.json`);
    if (!response.ok) throw new Error(`Failed to fetch playlist: ${response.status}`);
    return response.json();
  });
}

// Prediction types

export interface PredictionData {
  id: number;
  match_id: string;
  predictor_id: string;
  predicted_bot: string;
  correct?: boolean;
  created_at: string;
  resolved_at?: string;
}

export interface PredictionHistoryEntry {
  id: number;
  match_id: string;
  predicted_bot: string;
  predicted_name: string;
  correct: boolean | null;
  confidence?: number;
  created_at: string;
  resolved_at?: string;
  match_status: string;
  winner_name?: string;
}

export async function fetchPredictionHistory(predictorId: string, limit?: number): Promise<{ predictions: PredictionHistoryEntry[] }> {
  requireApiTransport('Prediction history');
  const params = new URLSearchParams({ predictor_id: predictorId });
  if (limit) params.set('limit', String(limit));
  const response = await fetch(`/api/predictions/history?${params}`);
  if (!response.ok) {
    await throwIfMatchTierOffline(response);
    throw new Error(`Failed to fetch prediction history: ${response.status}`);
  }
  if (!isJsonResponse(response)) throw new Error('Failed to fetch prediction history: non-JSON response');
  return response.json();
}

export interface PredictorStats {
  predictor_id: string;
  correct: number;
  incorrect: number;
  streak: number;
  best_streak: number;
}

export interface PredictionsLeaderboard {
  updated_at: string;
  entries: PredictorStats[];
}

export async function fetchPredictionsLeaderboard(): Promise<PredictionsLeaderboard> {
  return swr('predictions-leaderboard', async () => {
    const response = await fetch('/data/predictions/leaderboard.json');
    if (!response.ok) throw new Error(`Failed to fetch predictions leaderboard: ${response.status}`);
    return response.json();
  });
}

export interface OpenMatch {
  match_id: string;
  created_at: string;
  participants: { bot_id: string; name: string; rating: number }[];
  your_pick?: string;
}

export interface OpenPredictionsResponse {
  matches: OpenMatch[];
}

export async function fetchOpenPredictions(predictorId?: string): Promise<OpenPredictionsResponse> {
  requireApiTransport('Open match predictions');
  const params = predictorId ? `?predictor_id=${encodeURIComponent(predictorId)}` : '';
  const response = await fetch(`/api/predictions/open${params}`);
  if (!response.ok) {
    await throwIfMatchTierOffline(response);
    throw new Error(`Failed to fetch open predictions: ${response.status}`);
  }
  if (!isJsonResponse(response)) throw new Error('Failed to fetch open predictions: non-JSON response');
  return response.json();
}

export async function submitPrediction(matchId: string, botId: string, predictorId: string): Promise<{ id: number }> {
  requireApiTransport('Match predictions');
  const response = await fetch('/api/predict', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ match_id: matchId, bot_id: botId, predictor_id: predictorId }),
  });
  if (!response.ok) {
    await throwIfMatchTierOffline(response);
    const err = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || `Failed to submit prediction: ${response.status}`);
  }
  return response.json();
}

export function getOrCreatePredictorId(): string {
  let id = localStorage.getItem('acb_predictor_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('acb_predictor_id', id);
  }
  return id;
}

// Evolution meta types for homepage
export interface EvolutionMeta {
  generation: number;
  promoted_today: number;
  top_10_count: number;
  updated_at: string;
  matches_today?: number;   // plan §16.18: matches completed today
  active_bots?: number;     // plan §16.18: active bot count
}

export async function fetchEvolutionMeta(): Promise<EvolutionMeta> {
  return swr('evolution-meta', async () => {
    const response = await fetch('/data/evolution/meta.json');
    if (!response.ok) {
      const fallback: EvolutionMeta = { generation: 0, promoted_today: 0, top_10_count: 0, updated_at: '', matches_today: 0, active_bots: 0 };
      return fallback;
    }
    return response.json();
  });
}

// Season types (re-export from types.ts for convenience)
import type { SeasonIndex } from './types';
export type { Season, SeasonIndex } from './types';

export async function fetchSeasonIndex(): Promise<SeasonIndex> {
  return swr('season-index', async () => {
    const response = await fetch('/data/seasons/index.json');
    if (!response.ok) {
      return { updated_at: '', active_season: null, seasons: [] };
    }
    return response.json();
  });
}

// Commentary / Enrichment types (§13.3)

export interface CommentaryEntry {
  turn: number;
  text: string;
  type: 'setup' | 'action' | 'reaction' | 'climax' | 'denouement';
}

export interface EnrichedCommentary {
  match_id: string;
  generated_at: string;
  criteria: string[];
  entries: CommentaryEntry[];
}

export interface EnrichedMatchEntry {
  match_id: string;
  criteria: string[];
}

export interface EnrichedIndex {
  updated_at: string;
  entries: EnrichedMatchEntry[];
}

const R2_COMMENTARY_BASE = '/b2';

export async function fetchCommentary(matchId: string): Promise<EnrichedCommentary | null> {
  try {
    const response = await fetch(`${R2_COMMENTARY_BASE}/commentary/${matchId}.json`);
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

export async function fetchEnrichedIndex(): Promise<EnrichedIndex> {
  return swr('enriched-index', async () => {
    const response = await fetch('/data/commentary/index.json');
    if (!response.ok) return { updated_at: '', entries: [] };
    return response.json();
  });
}

// Rivalry types (matches data/meta/rivalries.json from index builder §13.5)
export interface RivalryBot {
  id: string;
  name: string;
}

export interface RivalryRecord {
  a_wins: number;
  b_wins: number;
  draws: number;
}

export interface RivalryStreak {
  holder: string;
  length: number;
}

export interface RivalryEntry {
  bot_a: RivalryBot;
  bot_b: RivalryBot;
  matches: number;
  record: RivalryRecord;
  closest_match?: string;
  longest_streak?: RivalryStreak;
  recent_matches: string[];
  narrative: string;
  score: number;
}

export interface RivalriesIndex {
  updated_at: string;
  rivalries: RivalryEntry[];
}

export async function fetchRivalries(): Promise<RivalriesIndex> {
  return swr('rivalries', async () => {
    const response = await fetch('/data/meta/rivalries.json');
    if (!response.ok) return { updated_at: '', rivalries: [] };
    return response.json();
  });
}

// Map voting types (§14.6)

export interface MapVoteResponse {
  map_id: string;
  vote: number;
  net_votes: number;
}

export interface MapVotesResponse {
  map_id: string;
  net_votes: number;
  my_vote?: number;
}

// ─── Community /api response envelopes (served by web/functions/api) ─────────────
// The Pages Function constructs its answers against these types
// (lib/api-backend.ts imports them), so a shape change fails the tsc gate
// on both sides instead of silently drifting from cmd/acb-api/server.go at
// runtime. The replay-feedback entry and round-trip types are FeedbackEntry
// and FeedbackResponse in types.ts — shared with the static-file fallback.

export interface ApiCapabilities {
  register: boolean;
  rotate_key: boolean;
  revoke_key: boolean;
  predictions: boolean;
  feedback: boolean;
  map_votes: boolean;
}

export interface ApiHealthResponse {
  status: 'ok';
  capabilities: ApiCapabilities;
}

/** Every error answer, any status. `code` marks a machine-readable subclass. */
export interface ApiErrorBody {
  error: string;
  code?: string;
}

export interface FeedbackRecordedResponse {
  status: 'recorded';
  feedback_id: string;
}

export type FeedbackUpvoteStatus = 'recorded' | 'already_upvoted';

export interface FeedbackUpvoteResponse {
  status: FeedbackUpvoteStatus;
}

export function getOrCreateVoterId(): string {
  let id = localStorage.getItem('acb_voter_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('acb_voter_id', id);
  }
  return id;
}

export async function submitMapVote(mapId: string, vote: 1 | -1): Promise<MapVoteResponse> {
  requireApiTransport('Map voting');
  const voterId = getOrCreateVoterId();
  const response = await fetch(`${API_BASE}/vote/map`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ map_id: mapId, voter_id: voterId, vote }),
  });
  if (!response.ok) {
    await throwIfMatchTierOffline(response);
    const err = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || `Failed to submit vote: ${response.status}`);
  }
  return response.json();
}

export async function fetchMapVotes(mapId: string): Promise<MapVotesResponse> {
  requireApiTransport('Map vote tallies');
  const voterId = getOrCreateVoterId();
  const response = await fetch(`${API_BASE}/vote/map/${encodeURIComponent(mapId)}?voter_id=${encodeURIComponent(voterId)}`);
  if (!response.ok) {
    await throwIfMatchTierOffline(response);
    throw new Error(`Failed to fetch map votes: ${response.status}`);
  }
  if (!isJsonResponse(response)) throw new Error('Failed to fetch map votes: non-JSON response');
  return response.json();
}

// Map browsing types (§14.6)

export interface MapData {
  map_id: string;
  player_count: number;
  status: string;
  engagement: number;
  wall_density: number;
  energy_count: number;
  grid_width: number;
  grid_height: number;
  net_votes: number;
  created_at: string;
}

export interface MapsIndex {
  updated_at: string;
  maps: MapData[];
}

export async function fetchMapsIndex(): Promise<MapsIndex> {
  const response = await fetch('/maps/index.json');
  if (!response.ok) throw new Error(`Failed to fetch maps index: ${response.status}`);
  return response.json();
}

// ─── ADR-001: Live-tail feed for match results and leaderboard freshness ─────────

// Live-tail match types (written by match worker to R2, consumed by SPA)
export interface LiveTailMatch {
  id: string;
  completed_at: string;
  participants: MatchSummaryParticipant[];
  winner_id: string | null;
  map_id?: string;
  turns: number | null;
  end_reason: string | null;
  enriched?: boolean;
}

export interface LiveTailFile {
  updated_at: string;
  matches: LiveTailMatch[]; // last ~50 completed matches
}

// Leaderboard delta types (rating changes since last full build)
export interface LeaderboardDeltaEntry {
  rating_delta: number;
  new_rating: number;
  new_rating_deviation?: number;
  new_matches_played?: number;
  new_matches_won?: number;
  new_win_rate?: number;
}

export interface LeaderboardDeltaFile {
  updated_at: string;
  deltas: Record<string, LeaderboardDeltaEntry>; // bot_id -> delta
}

// Fetch live-tail matches from R2 (bypass SWR - always fresh)
const R2_LIVE_BASE = '/r2';

export async function fetchLiveTailMatches(): Promise<LiveTailFile> {
  // Live-tail updates every ~10s — bypass SWR, always fetch fresh
  const response = await fetch(`${R2_LIVE_BASE}/matches/live-tail.json`);
  if (!response.ok) {
    // Return empty tail if not available (fallback to batch-only)
    return { updated_at: '', matches: [] };
  }
  return response.json();
}

// Fetch leaderboard deltas from R2 (bypass SWR - always fresh)
export async function fetchLeaderboardDeltas(): Promise<LeaderboardDeltaFile> {
  // Deltas update every ~10s — bypass SWR, always fetch fresh
  const response = await fetch(`${R2_LIVE_BASE}/leaderboard/live-delta.json`);
  if (!response.ok) {
    // Return empty deltas if not available (fallback to batch-only)
    return { updated_at: '', deltas: {} };
  }
  return response.json();
}

// Merge live-tail matches into batch match index (ADR-001 reconciliation)
export async function fetchMatchIndexWithTail(): Promise<MatchIndex> {
  const [batchIndex, liveTail] = await Promise.all([
    fetchMatchIndex(), // /data/matches/index.json
    fetchLiveTailMatches(), // /r2/matches/live-tail.json
  ]);

  // Dedupe: only prepend live-tail matches not already in batch index
  const existingIds = new Set(batchIndex.matches.map((m) => m.id));
  const newMatches = liveTail.matches.filter((m) => !existingIds.has(m.id));

  // Prepend new matches (most recent first)
  return {
    updated_at: liveTail.updated_at || batchIndex.updated_at,
    matches: [...newMatches, ...batchIndex.matches],
  };
}

// Apply leaderboard deltas to batch leaderboard (ADR-001 reconciliation)
export async function fetchLeaderboardWithDeltas(): Promise<LeaderboardIndex> {
  const [batchIndex, deltaFile] = await Promise.all([
    fetchLeaderboard(), // /data/leaderboard.json
    fetchLeaderboardDeltas(), // /r2/leaderboard/live-delta.json
  ]);

  // No deltas to apply - return batch as-is
  if (Object.keys(deltaFile.deltas).length === 0) {
    return batchIndex;
  }

  // Apply deltas to matching entries
  const entries = batchIndex.entries.map((entry) => {
    const delta = deltaFile.deltas[entry.bot_id];
    if (!delta) return entry;

    // Skip delta if it's stale (completed_at predates batch updated_at)
    // This prevents reapplying deltas already absorbed by the full build
    const batchUpdatedAt = new Date(batchIndex.updated_at).getTime();
    const deltaUpdatedAt = new Date(deltaFile.updated_at).getTime();
    if (deltaUpdatedAt < batchUpdatedAt) {
      return entry;
    }

    // Apply delta fields
    return {
      ...entry,
      rating: delta.new_rating,
      rating_deviation: delta.new_rating_deviation ?? entry.rating_deviation,
      matches_played: delta.new_matches_played ?? entry.matches_played,
      matches_won: delta.new_matches_won ?? entry.matches_won,
      win_rate: delta.new_win_rate ?? entry.win_rate,
    };
  });

  return {
    updated_at: deltaFile.updated_at || batchIndex.updated_at,
    entries,
  };
}
